// The binder share client (mtg-api plan 2026-10-26 "Share a binder", Phase 1;
// wire fixtures in src/api/__fixtures__/binder_share_wire/ and the
// binders_share_* / binders_list_shared entries of copies_wire/).
//
//  - Owner routes (mint, revoke) go through copiesFetch with
//    `capabilityNeutral`, like every binder call: a share answer (the 404 of a
//    gate that is off, say) never moves the per-copy capability.
//  - The public read (GET /shared-binders/{token}) needs NO auth and is read
//    with apiGet, `cache: 'no-store'` (the server sends no-store too: a revoke
//    must stop the page at once).
//  - The matches (POST /shared-binders/{token}/matches) need the VIEWER's
//    bearer, sent through withCollectionToken (one 401 refresh, only for the
//    same user; nothing is sent for a guest).
//
// Nothing here reads or writes the offline collection mirror: a shared binder
// is someone else's cards.
import { ApiError, apiGet, apiPost } from './client'
import { copiesFetch, responseJson, type CopiesContext } from './collectionCopies'
import { COPY_FINISHES, HTTP, SEG, type CopyFinish } from './collectionCopies.codes'
import {
  DEFAULT_DIR, SHARE_DETAIL, SHARE_QUERY, SHARE_SEG, SHARE_TOKEN_RE, SHARED_ERR, SHARED_PAGE_LIMIT,
  type PublicSort, type SharedErrKind, type SortDir,
} from './binderShare.codes'
import { withCollectionToken, CollectionAuthError } from '../lib/accessToken'

// --- Wire types ---------------------------------------------------------------

/** A binder's live public link (GET /binders, per binder). */
export interface BinderShare {
  token: string
  share_url: string
}

/** POST /collections/{id}/binders/{binder_id}/share. */
export interface BinderShareMint extends BinderShare {
  /** The binder already had this link (idempotent). */
  reused: boolean
}

export interface SharedBinderMeta {
  name: string
  kind: string
  color: string | null
  cover_image: string | null
}

/**
 * One AGGREGATED public row: the binder's lines grouped by their public
 * identity. It has no copy id; `row_key` is its stable key, `match_key` the
 * card's front key (what the matches name).
 */
export interface PublicRow {
  row_key: string
  match_key: string
  card_name: string
  scryfall_id: string | null
  set: string | null
  set_name: string | null
  collector_number: string | null
  rarity: string | null
  /** A finish this client does not know reads as nonfoil (the default printing). */
  finish: CopyFinish
  condition: string | null
  language: string | null
  qty: number
  unit_price_usd: number | null
  image_normal: string | null
  type_line: string | null
  colors: string[] | null
  color_identity: string[] | null
  mv: number | null
  /** A want line (shown with a Want badge, never counted in the value). */
  want: boolean
}

export interface SharedBinderPage {
  binder: SharedBinderMeta
  items: PublicRow[]
  next_cursor: string | null
  /** Totals over the WHOLE public membership (after q), never this page. */
  total_rows: number
  total_qty: number
  want_qty: number
  /** Decimal string; null when nothing is priced. */
  value_usd: string | null
  unpriced_qty: number
  as_of: string | null
}

export interface MatchWant {
  match_key: string
  card_name: string
  qty_wanted: number
  binder_qty: number
}

export interface MatchDeckNeed {
  match_key: string
  card_name: string
  qty_missing: number
  /** The first three deck names. */
  decks: string[]
  deck_count: number
}

export type BinderMatches =
  | { self: true }
  | {
    self: false
    wants: MatchWant[]
    /** null when the viewer's decks could not be read in full (never an empty "none needed"). */
    deck_needs: MatchDeckNeed[] | null
    decks_unavailable: boolean
    no_collection: boolean
  }

// --- Errors -------------------------------------------------------------------

/** A public read or matches failure, by kind (the page maps kinds to copy). */
export class SharedBinderError extends Error {
  readonly kind: SharedErrKind
  readonly status?: number
  readonly detail?: string

  constructor(kind: SharedErrKind, opts: { status?: number; detail?: string } = {}) {
    // eslint-disable-next-line i18next/no-literal-string -- developer message, never rendered
    super(`shared binder ${kind}${opts.status ? ` (${opts.status})` : ''}`)
    // eslint-disable-next-line i18next/no-literal-string -- class name
    this.name = 'SharedBinderError'
    this.kind = kind
    this.status = opts.status
    this.detail = opts.detail
  }
}

export function isSharedBinderError(e: unknown): e is SharedBinderError {
  return e instanceof SharedBinderError
}

/** An HTTP status and its detail code to a failure kind. */
export function classifySharedFailure(status: number, detail: string | undefined): SharedErrKind {
  if (status === 404) return SHARED_ERR.notFound
  if (status === 401) return SHARED_ERR.unauthorized
  if (status === 422 && detail === SHARE_DETAIL.badCursor) return SHARED_ERR.badCursor
  if (status === 422 && detail === SHARE_DETAIL.binderTooLarge) return SHARED_ERR.tooLarge
  if (status === 429) return SHARED_ERR.rateLimited
  if (status === 503 && detail === SHARE_DETAIL.decksUnavailable) return SHARED_ERR.decksUnavailable
  if (status === 503 && detail === SHARE_DETAIL.bindersNotReady) return SHARED_ERR.notReady
  if (status === 503) return SHARED_ERR.unavailable
  return SHARED_ERR.server
}

function fromThrown(e: unknown): SharedBinderError {
  if (e instanceof SharedBinderError) return e
  if (e instanceof ApiError) return new SharedBinderError(classifySharedFailure(e.status, e.detail), { status: e.status, detail: e.detail })
  if (e instanceof CollectionAuthError) return new SharedBinderError(SHARED_ERR.unauthorized)
  return new SharedBinderError(SHARED_ERR.network)
}

// --- Decoders -----------------------------------------------------------------

type Obj = Record<string, unknown>

function fail(): never {
  throw new SharedBinderError(SHARED_ERR.decode)
}
function obj(v: unknown): Obj {
  if (!v || typeof v !== 'object' || Array.isArray(v)) fail()
  return v as Obj
}
function str(v: unknown): string {
  if (typeof v !== 'string') fail()
  return v
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' ? v : null
}
function num(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) fail()
  return v
}
function numOr(v: unknown, d: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : d
}
function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}
function strList(v: unknown): string[] | null {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null
}
/** Money: a decimal string, a number read back into a string, or null. */
function dec(v: unknown): string | null {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return typeof v === 'string' && v.trim() ? v : null
}
/** An absolute https URL, else null (an image the page may render). */
function httpsOrNull(v: unknown): string | null {
  return typeof v === 'string' && /^https:\/\/\S+$/.test(v) ? v : null
}

/**
 * A binder's `share` from GET /binders: `{token, share_url}`, or null when
 * absent, null or malformed (a link this client cannot show is no link).
 */
export function decodeBinderShare(raw: unknown): BinderShare | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Obj
  if (typeof o.token !== 'string' || !SHARE_TOKEN_RE.test(o.token)) return null
  const url = httpsOrNull(o.share_url)
  if (!url) return null
  return { token: o.token, share_url: url }
}

export function decodeShareMint(raw: unknown): BinderShareMint {
  const share = decodeBinderShare(raw)
  if (!share) fail()
  return { ...share, reused: obj(raw).reused === true }
}

export function decodePublicRow(raw: unknown): PublicRow {
  const o = obj(raw)
  return {
    row_key: str(o.row_key),
    match_key: str(o.match_key),
    card_name: str(o.card_name),
    scryfall_id: strOrNull(o.scryfall_id),
    set: strOrNull(o.set),
    set_name: strOrNull(o.set_name),
    collector_number: strOrNull(o.collector_number),
    rarity: strOrNull(o.rarity),
    finish: (COPY_FINISHES as readonly unknown[]).includes(o.finish) ? o.finish as CopyFinish : COPY_FINISHES[0],
    condition: strOrNull(o.condition),
    language: strOrNull(o.language),
    qty: num(o.qty),
    unit_price_usd: numOrNull(o.unit_price_usd),
    image_normal: httpsOrNull(o.image_normal),
    type_line: strOrNull(o.type_line),
    colors: strList(o.colors),
    color_identity: strList(o.color_identity),
    mv: numOrNull(o.mv),
    want: o.want === true,
  }
}

export function decodeSharedBinderPage(raw: unknown): SharedBinderPage {
  const o = obj(raw)
  const b = obj(o.binder)
  if (!Array.isArray(o.items)) fail()
  return {
    binder: {
      name: str(b.name),
      kind: typeof b.kind === 'string' ? b.kind : '',
      color: strOrNull(b.color),
      cover_image: httpsOrNull(b.cover_image),
    },
    items: o.items.map(decodePublicRow),
    next_cursor: typeof o.next_cursor === 'string' && o.next_cursor ? o.next_cursor : null,
    total_rows: numOr(o.total_rows, 0),
    total_qty: numOr(o.total_qty, 0),
    want_qty: numOr(o.want_qty, 0),
    value_usd: dec(o.value_usd),
    unpriced_qty: numOr(o.unpriced_qty, 0),
    as_of: strOrNull(o.as_of),
  }
}

function decodeWant(raw: unknown): MatchWant {
  const o = obj(raw)
  return { match_key: str(o.match_key), card_name: str(o.card_name), qty_wanted: numOr(o.qty_wanted, 0), binder_qty: numOr(o.binder_qty, 0) }
}

function decodeNeed(raw: unknown): MatchDeckNeed {
  const o = obj(raw)
  const decks = strList(o.decks) ?? []
  return {
    match_key: str(o.match_key),
    card_name: str(o.card_name),
    qty_missing: numOr(o.qty_missing, 0),
    decks,
    deck_count: numOr(o.deck_count, decks.length),
  }
}

export function decodeBinderMatches(raw: unknown): BinderMatches {
  const o = obj(raw)
  if (o.self === true) return { self: true }
  const decksUnavailable = o.decks_unavailable === true
  return {
    self: false,
    wants: Array.isArray(o.wants) ? o.wants.map(decodeWant) : [],
    // Unavailable (or no list sent) is null, never an empty "your decks need nothing".
    deck_needs: decksUnavailable || !Array.isArray(o.deck_needs) ? null : o.deck_needs.map(decodeNeed),
    decks_unavailable: decksUnavailable || !Array.isArray(o.deck_needs),
    no_collection: o.no_collection === true,
  }
}

// --- Endpoints: owner ---------------------------------------------------------

const NEUTRAL = { capabilityNeutral: true } as const

/** `POST /binders/{binder_id}/share`: the binder's link, minted or reused (idempotent). */
export async function mintBinderShare(ctx: CopiesContext, binderId: number): Promise<BinderShareMint> {
  const res = await copiesFetch(ctx, { method: HTTP.post, sub: `${SEG.binders}/${binderId}${SHARE_SEG.share}`, ...NEUTRAL })
  return decodeShareMint(await responseJson(res))
}

/** `DELETE /binders/{binder_id}/share`: 204, idempotent. A new POST mints a NEW token. */
export async function revokeBinderShare(ctx: CopiesContext, binderId: number): Promise<void> {
  await copiesFetch(ctx, { method: HTTP.delete, sub: `${SEG.binders}/${binderId}${SHARE_SEG.share}`, ...NEUTRAL })
}

// --- Endpoints: public --------------------------------------------------------

export interface SharedBinderQuery {
  cursor?: string | null
  sort?: PublicSort
  dir?: SortDir
  q?: string
  limit?: number
}

/** The public read's path and query (shared with the OG function's peek). */
export function sharedBinderPath(token: string, query: SharedBinderQuery = {}): string {
  const qs = new URLSearchParams()
  if (query.cursor) qs.set(SHARE_QUERY.cursor, query.cursor)
  qs.set(SHARE_QUERY.limit, String(query.limit ?? SHARED_PAGE_LIMIT))
  if (query.sort) {
    qs.set(SHARE_QUERY.sort, query.sort)
    qs.set(SHARE_QUERY.dir, query.dir ?? DEFAULT_DIR[query.sort])
  }
  const q = query.q?.trim()
  if (q) qs.set(SHARE_QUERY.q, q)
  return `${SHARE_SEG.sharedBinders}${encodeURIComponent(token)}?${qs.toString()}`
}

/** `GET /shared-binders/{token}`: one page of the binder's public rows. No auth. */
export async function fetchSharedBinder(token: string, query: SharedBinderQuery = {}): Promise<SharedBinderPage> {
  if (!SHARE_TOKEN_RE.test(token)) throw new SharedBinderError(SHARED_ERR.notFound, { status: 404 })
  let raw: unknown
  try {
    // eslint-disable-next-line i18next/no-literal-string -- a fetch cache mode
    raw = await apiGet<unknown>(sharedBinderPath(token, query), { cache: 'no-store' })
  } catch (e) {
    throw fromThrown(e)
  }
  return decodeSharedBinderPage(raw)
}

/**
 * `POST /shared-binders/{token}/matches` as the signed-in viewer `userId`:
 * what they want or their decks need from the binder, or `{self: true}`
 * when it is their own. Nothing is sent without that user's token.
 */
export async function fetchBinderMatches(token: string, userId: string): Promise<BinderMatches> {
  if (!SHARE_TOKEN_RE.test(token)) throw new SharedBinderError(SHARED_ERR.notFound, { status: 404 })
  let raw: unknown
  try {
    raw = await withCollectionToken(userId, bearerToken => apiPost<unknown>(
      `${SHARE_SEG.sharedBinders}${encodeURIComponent(token)}${SHARE_SEG.matches}`, {}, { bearerToken },
    ))
  } catch (e) {
    throw fromThrown(e)
  }
  return decodeBinderMatches(raw)
}
